import { Field, Input, Stack } from '@components/ui';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import { HireStepActions } from '@features/buyer/hire/hire-r8tr/hire-step-actions';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import Box from '@mui/material/Box';
import { useEffect } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { JobDetailsFormValues } from './jobDetailsTypes';

interface JobDetailsProps extends HireStepNavigation {
  onPressNext: () => void;
  handleTosterFire: (message: string) => void;
}

const inputBinding = (
  registration: UseFormRegisterReturn
): Pick<UseFormRegisterReturn, 'name' | 'onBlur' | 'onChange'> & {
  inputRef: UseFormRegisterReturn['ref'];
} => ({
  name: registration.name,
  onBlur: registration.onBlur,
  onChange: registration.onChange,
  inputRef: registration.ref,
});

const JobDetails = ({
  onPressNext,
  handleTosterFire,
  onBack,
  onCancel,
}: JobDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { createJob, saveJob } = useHireJob();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<JobDetailsFormValues>({
    mode: 'onChange',
    defaultValues: {
      description: '',
      serialNumber: '',
      year: '',
      make: '',
      model: '',
    },
  });

  const isNonAutomotive = createJob?.isAutomotive === false;

  useEffect(() => {
    reset({
      description: createJob.description || '',
      serialNumber: createJob.vinOrSerial || '',
      year: createJob.year || '',
      make: createJob.make || '',
      model: createJob.model || '',
    });
  }, [createJob, reset]);

  const onSubmit = (values: JobDetailsFormValues): void => {
    if (
      !values.description ||
      (!isNonAutomotive && !values.year) ||
      !values.make ||
      !values.model
    ) {
      handleTosterFire(t('hire.fillRequired'));
      return;
    }
    saveJob({
      description: values.description,
      vinOrSerial: values.serialNumber,
      year: values.year,
      make: values.make,
      model: values.model,
      categoryId: createJob.categoryId,
      subCategoryId: createJob.subCategoryId,
      buyerCost: createJob.buyerCost,
      askingPrice: createJob.askingPrice,
      minimumCost: createJob.minimumCost,
      termsAccepted: createJob.termsAccepted,
    });
    onPressNext();
  };

  return (
    <Stack gap="xs" sx={{ flex: 1 }}>
      <Field
        label={
          isNonAutomotive
            ? t('hire.descriptionRequired')
            : t('hire.describeVehicle')
        }
      >
        <Input
          type="text"
          placeholder={
            isNonAutomotive
              ? t('hire.describeItemPlaceholder')
              : t('hire.describeVehiclePlaceholder')
          }
          {...inputBinding(register('description', { required: true }))}
        />
      </Field>

      <Box
        sx={{
          display: 'grid',
          columnGap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: isNonAutomotive ? '1fr 1fr' : '120px 1fr 1fr',
          },
        }}
      >
        {!isNonAutomotive ? (
          <Field label={t('hire.year')}>
            <Input
              type="text"
              placeholder={t('hire.yearPlaceholder')}
              inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              {...inputBinding(register('year', { required: true }))}
            />
          </Field>
        ) : null}

        <Field
          label={isNonAutomotive ? t('hire.makeBrand') : t('hire.make')}
        >
          <Input
            type="text"
            placeholder={
              isNonAutomotive
                ? t('hire.makeBrandPlaceholder')
                : t('hire.makePlaceholder')
            }
            {...inputBinding(register('make', { required: true }))}
          />
        </Field>

        <Field
          label={isNonAutomotive ? t('hire.modelItem') : t('hire.model')}
        >
          <Input
            type="text"
            placeholder={
              isNonAutomotive
                ? t('hire.modelItemPlaceholder')
                : t('hire.modelPlaceholder')
            }
            {...inputBinding(register('model', { required: true }))}
          />
        </Field>
      </Box>

      <Field
        label={
          isNonAutomotive ? t('hire.serialOptional') : t('hire.vinOptional')
        }
      >
        <Input
          type="text"
          placeholder={
            isNonAutomotive
              ? t('hire.serialPlaceholder')
              : t('hire.vinPlaceholder')
          }
          {...inputBinding(register('serialNumber'))}
        />
      </Field>

      <HireStepActions
        onBack={onBack}
        onCancel={onCancel}
        onPrimary={handleSubmit(onSubmit)}
        primaryDisabled={!isValid}
      />
    </Stack>
  );
};

export default JobDetails;
